/**
 * The support identity cookie: how the marketing site (sportsmet.com and
 * www.sportsmet.com, `apps/home`) learns who the app identified to the support
 * chat, so the same chat there knows the fan too.
 *
 * The session cookies stay host-only on app.sportsmet.com and are not widened.
 * This cookie is separate and holds exactly what the provider is sent and
 * nothing else: the identity auth-api signed (externalId, email, userHash)
 * plus the unsigned profile extras and tags. It never holds an access token,
 * a refresh token or any claim of one. The userHash is a credential toward
 * the provider only (it lets a browser open this fan's support conversations),
 * which is what it already is in the app.
 *
 * Written on `.sportsmet.com` so the apex and www hosts can read it, and only
 * from a page under sportsmet.com: on localhost, a preview or the e2e stack
 * nothing is written. `secure`, `SameSite=Lax`, `path=/`, and the session
 * cookies' thirty days, re-stamped on every page load that identifies the fan
 * so it slides with the session. Cleared, on the same domain and path,
 * wherever the app resets the chat or the identity goes away.
 *
 * `apps/home/src/lib/supportWidget.ts` reads it. The name, the version and the
 * shape are a contract between the two apps; `apps/home`'s test pins its copy
 * of the name to this file.
 */
import { SESSION_COOKIE_TTL_SECONDS } from "./auth/sessionCookies";
import type {
  typeSupportIdentity,
  typeSupportProfile,
  typeSupportTags,
} from "./supportWidget";

export const SUPPORT_IDENTITY_COOKIE_NAME = "sportsmet-support-identity";
export const SUPPORT_IDENTITY_COOKIE_DOMAIN = ".sportsmet.com";
export const SUPPORT_IDENTITY_COOKIE_VERSION = 1;

/**
 * The most the encoded value may take. Browsers allow about 4 KB per cookie,
 * and this one rides along on every request to every sportsmet.com host, so it
 * stays at half that. Extras are dropped, least useful first, to fit.
 */
export const SUPPORT_IDENTITY_COOKIE_MAX_BYTES = 2048;

export type typeSupportIdentityCookie = typeSupportIdentity &
  typeSupportProfile & {
    v: typeof SUPPORT_IDENTITY_COOKIE_VERSION;
    tags?: typeSupportTags;
  };

/** The `document` side this writes through, so a test reads every write. */
export type typeCookieJar = { cookie: string };

/**
 * The cookie domain for a page, or null when the page is not under
 * sportsmet.com and nothing is written. A suffix match on a whole label, so
 * `notsportsmet.com` is not a sportsmet host.
 */
export function supportIdentityCookieDomain(hostname: string): string | null {
  const host = hostname.toLowerCase();
  return host === "sportsmet.com" || host.endsWith(".sportsmet.com")
    ? SUPPORT_IDENTITY_COOKIE_DOMAIN
    : null;
}

/** Each field picked by name, so nothing a caller added comes along. */
function cookiePayload({
  identity,
  profile,
  tags,
}: {
  identity: typeSupportIdentity;
  profile: typeSupportProfile | null;
  tags: typeSupportTags | null;
}): typeSupportIdentityCookie {
  const payload: typeSupportIdentityCookie = {
    v: SUPPORT_IDENTITY_COOKIE_VERSION,
    externalId: identity.externalId,
    email: identity.email,
    userHash: identity.userHash,
  };
  if (profile?.name !== undefined) payload.name = profile.name;
  if (profile?.avatarUrl !== undefined) payload.avatarUrl = profile.avatarUrl;
  if (profile?.attributes !== undefined)
    payload.attributes = profile.attributes;
  if (tags !== null) payload.tags = { add: tags.add, remove: tags.remove };
  return payload;
}

/**
 * Least useful first: each step leaves out one more extra when the value
 * does not fit.
 */
const SHRINK_STEPS: ((
  payload: typeSupportIdentityCookie,
) => typeSupportIdentityCookie)[] = [
  ({ attributes: _attributes, ...rest }) => rest,
  ({ tags: _tags, ...rest }) => rest,
  ({ avatarUrl: _avatarUrl, ...rest }) => rest,
  ({ name: _name, ...rest }) => rest,
];

/**
 * The cookie value, URI encoded JSON, at most
 * `SUPPORT_IDENTITY_COOKIE_MAX_BYTES`. Extras are dropped one field at a time
 * until it fits; null when even the bare identity does not.
 */
export function encodeSupportIdentityCookie(input: {
  identity: typeSupportIdentity;
  profile: typeSupportProfile | null;
  tags: typeSupportTags | null;
}): string | null {
  let payload = cookiePayload(input);
  // encodeURIComponent leaves only ASCII, so its length is the byte count.
  let encoded = encodeURIComponent(JSON.stringify(payload));
  for (const shrink of SHRINK_STEPS) {
    if (encoded.length <= SUPPORT_IDENTITY_COOKIE_MAX_BYTES) return encoded;
    payload = shrink(payload);
    encoded = encodeURIComponent(JSON.stringify(payload));
  }
  return encoded.length <= SUPPORT_IDENTITY_COOKIE_MAX_BYTES ? encoded : null;
}

/**
 * Writes the cookie for the identified fan. Off sportsmet.com, or when even
 * the bare identity does not fit, nothing is written and false comes back
 * (an oversized value is cleared, so a stale one never outlives it).
 */
export function writeSupportIdentityCookie({
  jar,
  hostname,
  identity,
  profile,
  tags,
  now = Date.now(),
}: {
  jar: typeCookieJar;
  hostname: string;
  identity: typeSupportIdentity;
  profile: typeSupportProfile | null;
  tags: typeSupportTags | null;
  now?: number;
}): boolean {
  const domain = supportIdentityCookieDomain(hostname);
  if (domain === null) return false;
  const value = encodeSupportIdentityCookie({ identity, profile, tags });
  if (value === null) {
    clearSupportIdentityCookie({ jar, hostname });
    return false;
  }
  const expires = new Date(now + SESSION_COOKIE_TTL_SECONDS * 1000);
  jar.cookie = `${SUPPORT_IDENTITY_COOKIE_NAME}=${value}; Domain=${domain}; Path=/; Expires=${expires.toUTCString()}; Secure; SameSite=Lax`;
  return true;
}

/** Expires the cookie on the same domain and path it was written with. */
export function clearSupportIdentityCookie({
  jar,
  hostname,
}: {
  jar: typeCookieJar;
  hostname: string;
}): void {
  const domain = supportIdentityCookieDomain(hostname);
  if (domain === null) return;
  jar.cookie = `${SUPPORT_IDENTITY_COOKIE_NAME}=; Domain=${domain}; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax`;
}
